import { flexRender, type SortDirection, type Header, type RowData } from '@tanstack/react-table';
import { ChevronDown, ChevronUp, ChevronsUpDown, type LucideIcon } from 'lucide-react';
import { SORT_DIRECTION } from '@avanta/shared/constants/common';
import { cn } from '@avanta/shared/utils/cn';

const getSortingIcon = (sorted: SortDirection | false): LucideIcon => {
  if (sorted === SORT_DIRECTION.asc) {
    return ChevronUp;
  }
  if (sorted === SORT_DIRECTION.desc) {
    return ChevronDown;
  }
  return ChevronsUpDown;
};

interface SortableTableHeaderProps<T extends RowData> {
  header: Header<T, unknown>;
}

export const SortableTableHeader = <T extends RowData>({
  header,
}: SortableTableHeaderProps<T>): React.ReactElement | null => {
  if (header.isPlaceholder) {
    return null;
  }

  const { column } = header;
  const canSort = column.columnDef.enableSorting === true && column.getCanSort();
  const isFiltered = column.getIsFiltered();
  const sorted = column.getIsSorted();
  const view = flexRender(column.columnDef.header, header.getContext());

  if (!canSort) {
    return (
      <span className={cn('notranslate', isFiltered && 'text-teal-600')} translate="no">
        {view}
      </span>
    );
  }

  const SortingIcon = getSortingIcon(sorted);

  return (
    <button
      type="button"
      onClick={column.getToggleSortingHandler()}
      className={cn(
        'notranslate inline-flex h-6 items-center gap-2 text-xs font-medium text-denim-800',
        'rounded-sm outline-none hover:text-denim-500 focus-visible:ring-2 focus-visible:ring-denim-500/50',
        isFiltered && 'text-teal-600'
      )}
      translate="no"
    >
      <span>{view}</span>
      <SortingIcon aria-hidden="true" className="size-4 text-denim-500" strokeWidth={2} />
    </button>
  );
};
